<template>
  <section class="solutions-stage d-flex flex-column">
    <Alert
      v-if="hasMissingFields && isEditMode"
      class="mb-5"
      :text="missingFieldsText"
      :vuetifyProps="{
        color: 'yellow',
        icon: 'mdi-information',
      }"
    />
    <TranslationLoading v-if="isTranslating" />
    <v-expansion-panels v-model="activePanel" v-else>
      <v-expansion-panel
        v-for="(rootCause, index) in rootCauses"
        :key="index"
        :disabled="rootCause.status === 'missing'"
        :data-testid="`problemSolving-solution-item`"
        :data-tourid="`problem-solving-solution-${index}`"
      >
        <v-expansion-panel-header>
          <SolutionHeader :index="index" :status="rootCause.status" />
        </v-expansion-panel-header>

        <v-expansion-panel-content class="px-6 py-4 ma-0">
          <p class="h6 ma-0 pa-0">
            {{ $t('problem_solving.phases.improve') }}
          </p>
          <p
            class="ma-0 pa-0 solutions-stage__subtitle font-size-12 grey--text text--darken-1 mt-4 mb-6"
          >
            {{ $t('problem_solving.improve.description') }}
          </p>
          <p class="d-flex justify-end caption blue-grey--text mt-n10">
            * {{ $t('common.required_fields') }}
          </p>
          <SolutionRootCause
            :name="rootCause.name || ''"
            :type="getRootCauseType(rootCause)"
            :description="rootCause.description || ''"
          />
          <SolutionForm
            :tid="`solution${index + 1}`"
            :id="`content-${index}`"
            :isSaving="isSaving"
            :isEditMode="isEditMode"
            :rootCause="rootCause"
            :markRequiredFields="markRequiredFields"
            @rootCauseChange="handleRootCauseChange($event, index)"
            @trackEvent="trackEvent($event)"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <PhaseSection
      class="mt-10"
      data-tourid="problemSolving-final-solution-phase"
      :isEditMode="isEditMode"
      :title="$t('problem_solving.solutions.final_solution')"
      :description="$t('problem_solving.solutions.final_solution_description')"
      :hasNextButton="hasNextButton"
      :hasBackButton="hasBackButton"
      @next="$emit('next')"
      @back="$emit('back')"
    >
      <MediaContainer
        data-private
        slot="left"
        class="mt-6"
        :images="solutionMedia"
        :readOnly="!isEditMode || isSaving"
        @change="updateSolutionMedia"
      />
      <TranslationLoading slot="right" v-if="isTranslating" />

      <div slot="right" v-else>
        <TextQuestion
          data-private
          :question="
            `${$t('problem_solving.solutions.whats_the_solution')}${
              solutionIsRequired ? '*' : ''
            }`
          "
          :placeholder="
            $t('problem_solving.solutions.whats_the_solution_placeholder')
          "
          :value="solutionDescription"
          :isEditMode="isEditMode"
          :maxLength="$constants.problemSolving.solutionDescriptionMaxLength"
          :error="
            markRequiredFields && solutionIsRequired && !solutionDescription
          "
          rows="1"
          @focusOut="trackEvent('solutionDescription')"
          @input="updateSolutionDescription"
        >
        </TextQuestion>
        <Button
          v-if="!revFinished"
          color="primary"
          class="d-flex align-self-end align-center mt-2"
          :text="$t('rev.complete_rev')"
          :class="{ 'mt-6': !isEditMode }"
          :disabled="disableCompleteButton"
          @click="handleCompleteRev"
        />
      </div>
    </PhaseSection>
  </section>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import Alert from '@/components/Alert.vue'
import Button from '@/components/UI/Button'
import SolutionHeader from '@/containers/ProblemSolvingView/SolutionHeader/SolutionHeader.vue'
import SolutionForm from '@/containers/ProblemSolvingView/SolutionForm/SolutionForm.vue'
import SolutionRootCause from '@/containers/ProblemSolvingView/SolutionRootCause/SolutionRootCause.vue'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'

const IN_PROGRESS = 'inProgress'
const COMPLETED = 'completed'
const MISSING = 'missing'

export default {
  name: 'SolutionStage',

  props: {
    isEditMode: {
      type: Boolean,
      required: true,
    },

    rev: {
      type: Object,
      required: true,
    },

    isSaving: Boolean,
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    markRequiredFields: Boolean,
    canComplete: Boolean,
  },

  mixins: [trackerMixin],

  components: {
    Alert,
    PhaseSection,
    MediaContainer,
    SolutionHeader,
    SolutionForm,
    SolutionRootCause,
    TextQuestion,
    TranslationLoading,
    Button,
  },

  data() {
    return {
      activePanel: 0,
    }
  },

  created() {
    this.activePanel = this.getInitialExpandedItemIndex()
  },

  computed: {
    disableCompleteButton() {
      return !this.canComplete
    },

    solutionMedia() {
      return this.rev?.solutionMedia ?? []
    },

    solutionDescription() {
      return this.rev?.solutionDescription ?? ''
    },

    missingFieldsText() {
      return this.$t('rev.missing_fields_alert')
    },

    revFinished() {
      return this.rev?.status === 'completed'
    },

    solutionIsRequired() {
      return this.rev?.rootCauses?.some?.(
        rootCause => rootCause.correctiveActionWorked
      )
    },

    rootCauses() {
      const revRootCauses = _.get(this.rev, 'rootCauses') || []
      const rootCauses = _.isEmpty(revRootCauses) ? [{}] : revRootCauses
      return rootCauses.map(r => ({
        ...r,
        status: this.getRootCauseStatus(r),
      }))
    },

    hasMissingFields() {
      const { problemAnalysis = {}, rootCauses } = this.rev
      const { problem, containment, goals } = problemAnalysis

      if (!(problem && (containment || goals))) return true

      const hasAtLeastOneCompleteRootCause = rootCauses.reduce(
        (compliesCriteria, rootCause) => {
          if (compliesCriteria) return true
          return rootCause?.name?.trim().length > 0
        },
        false
      )

      if (!hasAtLeastOneCompleteRootCause) return true

      return false
    },

    hasOneSolutionCompleted() {
      return this.rootCauses.some(rootCause => rootCause.status === 'completed')
    },
  },

  methods: {
    ...mapMutations({
      updateRootCauses: 'problemSolving/updateRootCauses',
      updateSolutionMedia: 'problemSolving/updateSolutionMedia',
      updateSolutionDescription: 'problemSolving/updateSolutionDescription',
    }),

    getInitialExpandedItemIndex() {
      const index = _.findIndex(
        this.rootCauses,
        rootCause => rootCause.status !== 'missing'
      )
      if (index === -1) return 0
      return index
    },

    getRootCauseStatus(rootCause) {
      const {
        correctiveAction,
        correctiveActionWorked,
        name = '',
        preventiveAction,
        correctiveLearning,
      } = rootCause
      const correctiveActionWorkedHasAnswer = !_.isNil(correctiveActionWorked)
      if (!name?.trim()) return MISSING
      if (
        correctiveAction &&
        correctiveActionWorkedHasAnswer &&
        (preventiveAction || correctiveLearning)
      )
        return COMPLETED
      return IN_PROGRESS
    },

    getRootCauseType(rootCause) {
      const { type } = rootCause
      const typeData = this.$constants.problemSolving.rootCauseTypes.find(
        p => p.value === type
      )
      return typeData ? this.$t(typeData.name) : ''
    },

    handleRootCauseChange(rootCause, index) {
      const updatedRootCauses = _.cloneDeep(this.rootCauses)
      updatedRootCauses[index] = _.omit(rootCause, 'status')
      this.$emit('revChange')
      this.updateRootCauses(updatedRootCauses)
    },

    handleCompleteRev() {
      this.trackEvent('completeRev')
      this.$emit('completeRev')
    },

    trackEvent(changedField) {
      this.trackUpdate(this.rev, changedField)
    },
  },
}
</script>

<style lang="scss" scoped>
.solutions-stage {
  &__subtitle {
    max-width: 460px;
    line-height: 16px;
  }
}
</style>
